import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
// Color palette similar to Monday.com
const COLORS = [
    '#229954', // Green
    '#a4d65e', // Light Green
    '#f9b233', // Yellow
    '#ff9800', // Orange
    '#ef5350', // Red
    '#e91e63', // Pink
    '#9c27b0', // Purple
    '#3f51b5', // Indigo
    '#2196f3', // Blue
    '#00bcd4', // Cyan
    '#4db8a8', // Teal
    '#78909c', // Gray
    '#0073ea', // Dark Blue
    '#00c875', // Bright Green
    '#ff5e5b', // Bright Red
    '#fdab3d', // Bright Orange
    '#579bfc', // Light Blue
];
export const GroupEditModal = ({ isOpen, group, onClose, onSave, isDark = true, }) => {
    const [editedName, setEditedName] = useState('');
    const [selectedColor, setSelectedColor] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    useEffect(() => {
        if (group) {
            setEditedName(group.name);
            setSelectedColor(group.color);
            setError('');
        }
    }, [group, isOpen]);
    const handleSave = async () => {
        if (!group)
            return;
        if (!editedName.trim()) {
            setError('Group name cannot be empty');
            return;
        }
        setIsSaving(true);
        try {
            await onSave({
                ...group,
                name: editedName.trim(),
                color: selectedColor,
            });
            console.log('[Group Updated]', {
                groupId: group.id,
                oldName: group.name,
                newName: editedName.trim(),
                oldColor: group.color,
                newColor: selectedColor,
            });
            onClose();
        }
        catch (err) {
            setError('Failed to save group. Please try again.');
            console.error('Error saving group:', err);
        }
        finally {
            setIsSaving(false);
        }
    };
    if (!isOpen || !group)
        return null;
    return (_jsx("div", { className: "fixed inset-0 bg-black/50 flex items-center justify-center z-50", onClick: onClose, children: _jsxs("div", { className: `${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 w-full max-w-md shadow-lg`, onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "flex items-center justify-between mb-6", children: [_jsx("h2", { className: `text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`, children: "Edit Group" }), _jsx("button", { onClick: onClose, className: `p-1 rounded transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`, children: _jsx(X, { className: "w-5 h-5" }) })] }), _jsxs("div", { className: "mb-6", children: [_jsx("label", { className: `block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`, children: "Group Name" }), _jsx("input", { type: "text", value: editedName, onChange: (e) => setEditedName(e.target.value), placeholder: "Enter group name", className: `w-full px-3 py-2 rounded border transition-colors ${isDark
                                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:border-blue-400'
                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:outline-none focus:border-blue-500'}`, onKeyDown: (e) => {
                                if (e.key === 'Enter') {
                                    handleSave();
                                }
                            } })] }), _jsxs("div", { className: "mb-6", children: [_jsx("label", { className: `block text-sm font-medium mb-3 ${isDark ? 'text-gray-200' : 'text-gray-700'}`, children: "Group Color" }), _jsx("div", { className: "grid grid-cols-6 gap-2", children: COLORS.map((color) => (_jsx("button", { onClick: () => setSelectedColor(color), className: `w-10 h-10 rounded-lg transition-all ${selectedColor === color
                                    ? 'ring-2 ring-white ring-offset-2 scale-110'
                                    : 'hover:scale-105'} ${!isDark && selectedColor === color ? 'ring-offset-gray-100' : ''}`, style: { backgroundColor: color }, title: color }, color))) })] }), _jsxs("div", { className: "mb-6 flex items-center gap-3", children: [_jsx("span", { className: `text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`, children: "Selected Color:" }), _jsx("div", { className: "w-8 h-8 rounded-full border-2 border-gray-400", style: { backgroundColor: selectedColor } }), _jsx("code", { className: `text-xs font-mono ${isDark ? 'text-gray-400 bg-gray-700' : 'text-gray-600 bg-gray-100'} px-2 py-1 rounded`, children: selectedColor })] }), error && (_jsx("div", { className: "mb-4 p-3 bg-red-500/20 border border-red-500 rounded text-red-400 text-sm", children: error })), _jsxs("div", { className: "flex gap-3", children: [_jsx("button", { onClick: onClose, className: `flex-1 px-4 py-2 rounded font-medium transition-colors ${isDark
                                ? 'bg-gray-700 hover:bg-gray-600 text-white'
                                : 'bg-gray-200 hover:bg-gray-300 text-gray-900'}`, children: "Cancel" }), _jsx("button", { onClick: handleSave, disabled: isSaving, className: `flex-1 px-4 py-2 rounded font-medium transition-colors ${isSaving
                                ? isDark ? 'bg-blue-900 text-blue-200 cursor-not-allowed' : 'bg-blue-200 text-blue-600 cursor-not-allowed'
                                : isDark
                                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                    : 'bg-blue-500 hover:bg-blue-600 text-white'}`, children: isSaving ? 'Saving...' : 'Save' })] })] }) }));
};
